import React, { Suspense, useEffect } from "react";
import { useDispatch } from "react-redux";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import axios from "axios";
import { Layout } from "components/layouts/Layout";
import { changeCountry } from "store/actions/country";
import { NoAuthRoute } from "views/miCuenta/router/NoAuthRoute";
import { NotFoundScreen } from "views/NotFoundScreen";
import { LoadingScreen } from "views/LoadingScreen";

import MetaTags from "components/head/MetaTags";
import HomeScreen from "views/Home/HomeScreen";
import VehicleDetail from "views/VehicleDetail/VehicleDetail";
import NewCatalog from "../components/catalog/NewCatalog";

import ScrollToTop from "../components/scrollToTop/ScrollToTop";

const MiCuentaRoute = React.lazy(
  () => import("views/miCuenta/router/MiCuentaRoute")
);

const TermsScreen = React.lazy(() => import("views/PrivacyAndTerms/TermsScreen"));
const PrivacyScreen = React.lazy(() => import("views/PrivacyAndTerms/PrivacyScreen"));
// Temp: ozocio inactive
// const OzocioHomeScreen = React.lazy(
//   () => import("views/ozocioHome/OzocioHomeScreen")
// );
// const OzocioFormScreen = React.lazy(
//   () => import("views/ozocioForm/OzocioFormScreen")
// );
const OzonFlyScreen = React.lazy(() => import("views/ozonFly/OzonFlyScreen"));
const RejectedView = React.lazy(
  () => import("views/ozonFly/rejectedView/RejectedView")
);
const ContinueView = React.lazy(
  () => import("views/ozonFly/continueView/ContinueView")
);
const SummaryView = React.lazy(
  () => import("views/ozonFly/summaryView/SummaryView")
);

const LoanApplication = React.lazy(
  () => import("views/LoanApplication/LoanApplication")
);
const LoanApplicationIncomes = React.lazy(
  () => import("views/LoanApplicationIncomes/LoanApplicationIncomes")
);
const LoanApplicationApproved = React.lazy(
  () => import("views/LoanApplicationApproved/LoanApplicationApproved")
);
const LoanApplicationVehicles = React.lazy(
  () => import("views/LoanApplicationVehicles/LoanApplicationVehicles")
);
const LoanApplicationDocuments = React.lazy(
  () => import("views/LoanApplicationDocuments/LoanApplicationDocuments")
);
const LoanApplicationFinal = React.lazy(
  () => import("views/LoanApplicationFinal/LoanApplicationFinal")
);

const FormApplicationWithProvider = React.lazy(
  () => import("views/formApplication/FormApplicationWithProvider")
);

const FormApplicationWithAlliance = React.lazy(
  () => import("views/formApplication/FormApplicationWithAlliance")
);

const CatalogWithAlliance = React.lazy(
  () => import("views/Catalog/CatalogWithAlliance")
);

const CatalogDetailWithAlliance = React.lazy(
  () => import("views/Catalog/CatalogDetailWithAlliance")
);

const LoginScreen = React.lazy(() => import("views/auth/login/LoginScreen"));
const ActivateMiCuentaScreen = React.lazy(
  () => import("views/auth/activateAcccount/ActivateMiCuenta")
);
const RecoverPasswordScreen = React.lazy(
  () => import("views/auth/recoverPassword/RecoverPasswordScreen")
);
const MiCuentaScreen = React.lazy(
  () => import("views/miCuenta/MiCuentaScreen")
);
const ApplicationScreen = React.lazy(
  () => import("views/miCuenta/application/ApplicationScreen")
);
const ApplicationDocumentsScreen = React.lazy(
  () => import("views/miCuenta/applicationDocuments/ApplicationDocumentsScreen")
);
const FaqsScreen = React.lazy(() => import("views/faqs/FaqsScreen"));
const AboutUsScreen = React.lazy(() => import("views/AboutUs/AboutUsScreen"));
const ReferralsScreen = React.lazy(() => import("views/Referrals/ReferralsScreen"));

const ImmersiveFormApplication = React.lazy(
  () => import("views/ImmersiveFormApplication/ImmersiveFormApplication")
);

export const RootRouter = () => {
  const dispatch = useDispatch();
  const getGeoInfo = () => {
    axios
      .get("https://ipapi.co/json/")
      .then((response) => {
        const { data } = response;

        let isoCountry = "";
        if (data.country === "MX" || data.country === "CO") {
          isoCountry = data.country === "MX" ? "MX" : "CO";
        } else {
          isoCountry = "MX";
        }

        localStorage.setItem("country-ozon", `${isoCountry}`);
        dispatch(changeCountry(isoCountry));
      })
      .catch((error) => {
        console.error(error);
      });
  };

  useEffect(() => {
    getGeoInfo();
  }, [dispatch]);

  return (
    <BrowserRouter>
      <MetaTags
        title="Compra y vende tu moto nueva o seminueva a crédito en CDMX"
        description="Financia tu moto nueva o seminueva sin enganche en CDMX. Compra y venta fácil, pagos cómodos, entrega inmediata en Ozon."
      />
      <ScrollToTop />
      <Layout>
        <Suspense fallback={<LoadingScreen />}>
          <Routes>
            {/* Landing pages */}
            <Route path="/" element={<HomeScreen />} />
            <Route path="/faqs" element={<FaqsScreen />} />
            <Route path="/terminos-y-condiciones" element={<TermsScreen />} />
            <Route path="/aviso-de-privacidad" element={<PrivacyScreen />} />
            <Route path="/nosotros" element={<AboutUsScreen />} />
            <Route path="/referidos" element={<ReferralsScreen />} />

            {/*
              Vende tu moto — temporalmente off (redirige a /).
              Para reactivar: descomentar imports OzocioHomeScreen / OzocioFormScreen arriba,
              borrar las dos Route con Navigate y descomentar:
            <Route path="/vende-tu-moto" element={<OzocioHomeScreen />} />
            <Route
              path="/formulario-vende-tu-moto"
              element={<OzocioFormScreen />}
            />
            */}
            <Route
              path="/vende-tu-moto"
              element={<Navigate to="/" replace />}
            />
            <Route
              path="/formulario-vende-tu-moto"
              element={<Navigate to="/" replace />}
            />

            {/* Catalog */}
            <Route path="/catalogo" element={<NewCatalog />} />
            <Route path="/catalogo/:id" element={<VehicleDetail />} />
            <Route
              path="/:alianza/catalogo"
              element={<CatalogWithAlliance />}
            />
            <Route
              path="/:alianza/catalogo/:id"
              element={<CatalogDetailWithAlliance />}
            />

            {/* Ozoner */}
            <Route path="/solicitud-credito" element={<LoanApplication />} />
            <Route
              path="/solicitud-credito/comprobantes-de-ingreso"
              element={<LoanApplicationIncomes />}
            />
            <Route
              path="/solicitud-credito/validacion-ingresos-aprobada"
              element={<LoanApplicationApproved />}
            />
            <Route
              path="/solicitud-credito/vehiculos"
              element={<LoanApplicationVehicles />}
            />
            <Route
              path="/solicitud-credito/documentos"
              element={<LoanApplicationDocuments />}
            />
            <Route
              path="/solicitud-credito/final"
              element={<LoanApplicationFinal />}
            />

            <Route
              path="/financia-tu-moto"
              element={<FormApplicationWithProvider />}
            />
            <Route
              path="/:alianza/financia-tu-moto"
              element={<FormApplicationWithAlliance />}
            />
            <Route
              path="/financia-tu-moto/referido/:referralCode"
              element={<FormApplicationWithProvider />}
            />
            <Route
              path="/financia-tu-moto/documentos/:formId"
              element={<OzonFlyScreen />}
            />
            <Route path="/rechazo-solicitud" element={<RejectedView />} />
            <Route path="/documentos-en-proceso" element={<ContinueView />} />
            <Route path="/solicitud-en-proceso" element={<SummaryView />} />

            {/* Auth */}
            <Route element={<NoAuthRoute />}>
              <Route path="/ingresar" element={<LoginScreen />} />
              <Route
                path="/recuperar-contrasena"
                element={<RecoverPasswordScreen />}
              />
              <Route
                path="/activar-cuenta"
                element={<ActivateMiCuentaScreen />}
              />
            </Route>

            {/* Mi Cuenta */}
            <Route element={<MiCuentaRoute />}>
              <Route path="/mi-cuenta" element={<MiCuentaScreen />} />
              <Route
                path="/mi-cuenta/solicitud"
                element={<ApplicationScreen />}
              />
              <Route
                path="/mi-cuenta/documentos"
                element={<ApplicationDocumentsScreen />}
              />
            </Route>

            {/* Legacy */}
            <Route path="/offererdate" element={<Navigate replace to="/" />} />
            <Route
              path="/privacy"
              element={<Navigate replace to="/aviso-de-privacidad" />}
            />
            <Route path="/micuenta" element={<Navigate replace to="/" />} />
            <Route path="/micuenta/:id" element={<Navigate replace to="/" />} />
            <Route
              path="/recover-password"
              element={<Navigate replace to="/" />}
            />
            <Route path="/palenca" element={<Navigate replace to="/" />} />
            <Route
              path="/validateIdentity/:formId"
              element={
                <Navigate replace to="/financia-tu-moto/documentos/:formId" />
              }
            />
            <Route
              path="/validateIdentity"
              element={<Navigate replace to="/" />}
            />
            <Route
              path="/continueForm"
              element={<Navigate replace to="/documentos-en-proceso" />}
            />
            <Route
              path="/summaryForm"
              element={<Navigate replace to="/solicitud-en-proceso" />}
            />
            <Route
              path="/financia-tu-moto/results/:selectedVehicleId?"
              element={<Navigate replace to="/" />}
            />
            <Route
              path="/tyc"
              element={<Navigate replace to="/terminos-y-condiciones" />}
            />
            <Route
              path="/vehiculo-no-disponible"
              element={<Navigate replace to="/" />}
            />
            <Route
              path="/solicita-tu-credito"
              element={<ImmersiveFormApplication />}
            />
            <Route
              path="/solicita-tu-credito/referido/:referralCode"
              element={<ImmersiveFormApplication />}
            />

            <Route path="*" element={<NotFoundScreen />} />
          </Routes>
        </Suspense>
      </Layout>
    </BrowserRouter>
  );
};
